import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, Save, Copy } from "lucide-react";
import { getCurrency, getMinNotionalWarning } from './ExchangeUIConfigs';
import { calculateFirstEntryNotionalByMode } from './DcaUtils';

// Modular Sub-components
import { StrategyPresets } from './settings/StrategyPresets';
import { BasicSettings } from './settings/BasicSettings';
import { ScalingSettings } from './settings/ScalingSettings';
import { RiskSettings } from './settings/RiskSettings';
import { FilterSettings } from './settings/FilterSettings';
import { NotificationSettings } from './settings/NotificationSettings';

interface SettingsPanelProps {
  bot: any;
  onSaved: (updatedBot?: any) => void;
  is_testnet?: boolean;
  disable_all_testnet?: boolean;
  enabled_exchanges?: Record<string, boolean>;
  testnet_min_notional?: number;
  onClone?: (bot: any) => void;
}

export const SettingsPanel: React.FC<SettingsPanelProps> = ({ bot, onSaved, is_testnet, disable_all_testnet, enabled_exchanges, testnet_min_notional, onClone }) => {
  const currency = getCurrency(bot.symbol);
  const [form, setForm] = useState({
    name: bot.name || '',
    tp_pct: bot.tp_pct ?? 1,
    tp_pct_override: (bot.settings || {})['tp_pct_override'] ?? 0,
    sl_pct: bot.sl_pct ?? 0,
    step_pct: bot.step_pct ?? 1,
    multiplier: bot.multiplier ?? 2,
    max_scaling: bot.max_scaling ?? 7,
    leverage: bot.leverage ?? 1,
    base_qty: bot.base_qty ?? 10,
    balance: bot.balance ?? 0,
    use_allocated_balance: bot.use_allocated_balance ?? true,
    cyclic: bot.cyclic ?? true,
    stop_bot_on_sl: bot.stop_bot_on_sl ?? false,
    stop_bot_on_liquidation: bot.stop_bot_on_liquidation ?? false,
    fee_pct: bot.fee_pct ?? 0.04,
    limit_fee_pct: bot.limit_fee_pct ?? 0.02,
    interval: bot.interval || '5m',
    is_live: bot.is_live ?? false,
    sl_strategy: bot.sl_strategy || 'candle_close',
    entry_type: bot.entry_type || (bot.settings || {})['entry_type'] || 'fixed',
    entry_amount: bot.entry_amount ?? (bot.settings || {})['entry_amount'] ?? bot.base_qty ?? 10,
    tp_threshold_step: bot.tp_threshold_step || (bot.settings || {})['tp_threshold_step'] || 0,
    tp_threshold_pct: bot.tp_threshold_pct || (bot.settings || {})['tp_threshold_pct'] || 0,
    use_dynamic_tp: (bot.tp_threshold_step || (bot.settings || {})['tp_threshold_step'] || 0) > 0,
    custom_targets: (bot.settings || {})['custom_targets'] || [],
    stop_price_upper: (bot.settings || {})['stop_price_upper'] || 0,
    stop_price_upper_side: (bot.settings || {})['stop_price_upper_side'] || 'both',
    stop_price_lower: (bot.settings || {})['stop_price_lower'] || 0,
    stop_price_lower_side: (bot.settings || {})['stop_price_lower_side'] || 'both',
    scaling_mode: bot.scaling_mode || (bot.settings || {})['scaling_mode'] || ((bot.settings || {})['custom_targets']?.length > 0 ? 'custom' : 'auto'),
    use_ma_filter: bot.use_ma_filter ?? false,
    use_ma5_filter: bot.use_ma5_filter ?? false,
    ma_period: bot.ma_period || (bot.settings || {})['ma_period'] || 25,
    use_rsi_scaling: (bot.settings || {})['use_rsi_scaling'] ?? false,
    use_rsi_entry: (bot.settings || {})['use_rsi_entry'] ?? false,
    rsi_threshold: bot.rsi_threshold || (bot.settings || {})['rsi_threshold'] || 30,
    use_sequential_scaling: (bot.settings || {})['use_sequential_scaling'] ?? true,
    use_dynamic_targeting: (bot.settings || {})['use_dynamic_targeting'] ?? true,
    use_breakeven_timeout: (bot.settings || {})['use_breakeven_timeout'] ?? true,
    breakeven_target_roe: (bot.settings || {})['breakeven_target_roe'] ?? 1.6,
    use_disparity_gap: (bot.settings || {})['use_disparity_gap'] ?? false,
    disparity_factor: (bot.settings || {})['disparity_factor'] ?? 5.0,
    use_auto_slashing: (bot.settings || {})['use_auto_slashing'] ?? (bot.settings || {})['use_slashing'] ?? false,
    auto_slashing_health: (bot.settings || {})['auto_slashing_health'] ?? (bot.settings || {})['slashing_margin_threshold'] ?? 130,
    auto_slashing_pct: (bot.settings || {})['auto_slashing_pct'] ?? (bot.settings || {})['slashing_reduction_pct'] ?? 20,
    auto_slashing_mmr: (bot.settings || {})['auto_slashing_mmr'] ?? 0.5,
    use_limit_tp: (bot.settings || {})['use_limit_tp'] ?? false,
    pullback_pct: (bot.settings || {})['pullback_pct'] ?? 0.1,
    use_partial_tp: (bot.settings || {})['use_partial_tp'] ?? false,
    partial_tp_rate1: (bot.settings || {})['partial_tp_rate1'] ?? 66.66,
    partial_tp_qty_rate1: (bot.settings || {})['partial_tp_qty_rate1'] ?? 50.0,
    use_breakeven_after_partial: (bot.settings || {})['use_breakeven_after_partial'] ?? true,
    min_partial_tp_pct: (bot.settings || {})['min_partial_tp_pct'] ?? 10.0,
    use_profit_offsetting: (bot.settings || {})['use_profit_offsetting'] ?? true,
    profit_offset_pct: (bot.settings || {})['profit_offset_pct'] ?? 50.0,
    use_net_profit_tp: (bot.settings || {})['use_net_profit_tp'] ?? true,
    exchange: bot.exchange || 'binance',
    margin_mode: bot.margin_mode || (bot.settings || {})['margin_mode'] || 'crossed',
    immediate_reentry: (bot.settings || {})['immediate_reentry'] ?? false,
    use_stoch_alignment_entry: (bot.settings || {})['use_stoch_alignment_entry'] ?? false,
    use_1h_trend_filter: (bot.settings || {})['use_1h_trend_filter'] ?? false,
    use_1h_trend_sl: (bot.settings || {})['use_1h_trend_sl'] ?? false,
    use_1h_trend_be: (bot.settings || {})['use_1h_trend_be'] ?? false,
    use_1h_sideways_filter: (bot.settings || {})['use_1h_sideways_filter'] ?? false,
    use_1h_sideways_scaling_filter: (bot.settings || {})['use_1h_sideways_scaling_filter'] ?? false,
    adx_threshold: (bot.settings || {})['adx_threshold'] ?? 22,
    use_atr_scaling: (bot.settings || {})['use_atr_scaling'] ?? false,
    atr_multiplier: (bot.settings || {})['atr_multiplier'] ?? 2.0,
    atr_period: (bot.settings || {})['atr_period'] ?? 14,
    atr_min_gap_ratio: (bot.settings || {})['atr_min_gap_ratio'] ?? 0.5,
    atr_limit_mode: (bot.settings || {})['atr_limit_mode'] ?? 'strict',
    use_super_ma_filter: (bot.settings || {})['use_super_ma_filter'] ?? true,
    use_super_ma_first_entry: (bot.settings || {})['use_super_ma_first_entry'] ?? true,
    use_super_ma_be: (bot.settings || {})['use_super_ma_be'] ?? true,
    use_super_ma_scaling: (bot.settings || {})['use_super_ma_scaling'] ?? true,
    use_super_ma_slow_mode: (bot.settings || {})['use_super_ma_slow_mode'] ?? true,
    super_ma_score_threshold: (bot.settings || {})['super_ma_score_threshold'] ?? 3,
    use_ts_entry: (bot.settings || {})['use_ts_entry'] ?? false,
    telegram_enabled: bot.telegram_enabled ?? null,
    telegram_notify_types: bot.telegram_notify_types || ['entry', 'scaling', 'tp', 'sl', 'liquidate', 'error', 'info'],
    entry_price_limit_long: (bot.settings || {})['entry_price_limit_long'] || 0,
    entry_price_limit_short: (bot.settings || {})['entry_price_limit_short'] || 0,
    one_way_mode: (bot.settings || {})['one_way_mode'] ?? false,
    use_rebound_reduction: (bot.settings || {})['use_rebound_reduction'] ?? false,
    rebound_trigger_step: (bot.settings || {})['rebound_trigger_step'] ?? 2,
    rebound_pct: (bot.settings || {})['rebound_pct'] ?? 0.5,
    rebound_reduction_ratio: (bot.settings || {})['rebound_reduction_ratio'] ?? 50.0,
    use_variable_stop_loss: (bot.settings || {})['use_variable_stop_loss'] ?? false,
    variable_sl_threshold_step: (bot.settings || {})['variable_sl_threshold_step'] ?? 4,
    variable_sl_low_pct: (bot.settings || {})['variable_sl_low_pct'] ?? 55.0,
    variable_sl_high_pct: (bot.settings || {})['variable_sl_high_pct'] ?? 70.0,
    use_trailing_tp: (bot.settings || {})['use_trailing_tp'] ?? false,
    trailing_callback_pct: (bot.settings || {})['trailing_callback_pct'] ?? 0.2,
    trailing_activation_pct: (bot.settings || {})['trailing_activation_pct'] ?? '',
    use_skip_hours: (bot.settings || {})['use_skip_hours'] ?? false,
    skip_time_segments: (bot.settings || {})['skip_time_segments'] || [],
    use_skip_days: (bot.settings || {})['use_skip_days'] ?? false,
    skip_days: (bot.settings || {})['skip_days'] || [],
  });

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [showCustomTargets, setShowCustomTargets] = useState(form.custom_targets.length > 0);
  const [scalingMode, setScalingMode] = useState(form.scaling_mode);

  const isRunning = bot.is_active || bot.realtime_status === 'running' || bot.realtime_status === 'queued';
  const hasPosition = (bot.active_cycles_details?.length || 0) > 0;
  const isReadOnly = bot.disabled || bot.archived;
  const isMarginReadOnly = isReadOnly || hasPosition;

  const firstEntryNotional = calculateFirstEntryNotionalByMode(form, scalingMode);
  const minNotionalWarning = getMinNotionalWarning(form.exchange, form.is_live, bot.use_testnet, firstEntryNotional, testnet_min_notional);

  const applyPreset = (type: 'hybrid' | 'martingale' | 'scalping' | 'rebound_shield') => {
    let updates: any = {};
    if (type === 'hybrid') {
      updates = {
        multiplier: 1.5, step_pct: 0.6, max_scaling: 10, tp_pct: 15.0,
        tp_threshold_step: 7, tp_threshold_pct: 10.0, use_dynamic_tp: true,
        leverage: 2,
        use_rsi_entry: true, rsi_threshold: 35, use_rsi_scaling: true,
        use_super_ma_filter: true, super_ma_score_threshold: 3,
        use_stoch_alignment_entry: true, use_1h_trend_filter: true,
        use_1h_sideways_filter: true, use_1h_sideways_scaling_filter: true,
        use_limit_tp: true, pullback_pct: 0.1, use_profit_offsetting: true
      };
    } else if (type === 'martingale') {
      updates = {
        multiplier: 2.0, step_pct: 1.0, max_scaling: 7, tp_pct: 1.0, use_dynamic_tp: false,
        leverage: 5,
        use_rsi_entry: false, use_rsi_scaling: false,
        use_super_ma_filter: false, use_1h_trend_filter: false,
        use_1h_sideways_filter: false, use_1h_sideways_scaling_filter: false,
        use_stoch_alignment_entry: false, use_limit_tp: false
      };
    } else if (type === 'scalping') {
      updates = {
        multiplier: 1.3, step_pct: 0.5, max_scaling: 15, tp_pct: 0.5, use_dynamic_tp: false,
        leverage: 10,
        use_rsi_entry: true, rsi_threshold: 30, use_rsi_scaling: true,
        use_super_ma_filter: true, super_ma_score_threshold: 2,
        use_stoch_alignment_entry: true, use_limit_tp: true, pullback_pct: 0.05
      };
    } else if (type === 'rebound_shield') {
      updates = {
        multiplier: 1.15, step_pct: 1.0, max_scaling: 10, tp_pct: 1.0, use_dynamic_tp: false,
        leverage: 5,
        use_rsi_entry: false, rsi_threshold: 30, use_rsi_scaling: false,
        use_super_ma_filter: true, super_ma_score_threshold: 3,
        use_limit_tp: true,
        use_rebound_reduction: true, rebound_trigger_step: 2, rebound_pct: 0.5, rebound_reduction_ratio: 50.0,
        use_trailing_tp: true, trailing_callback_pct: 0.2, trailing_activation_pct: 1.0
      };
    }
    setForm(f => ({ ...f, ...updates }));
    setMessage({ type: 'ok', text: '프리셋이 적용되었습니다.' });
    setTimeout(() => setMessage(null), 3000);
  };

  const syncAutoToCustom = () => {
    let cumulativeDist = 0;
    const latestPrice = Number(bot.current_price || bot.latest_price || 0);

    const newTargets = Array.from({ length: Number(form.max_scaling) || 0 }).map((_, i) => {
      const isPercent = form.entry_type === 'percent';
      let balanceBasis = Number(bot.principal_amount || form.balance || bot.total_equity || 0);
      if (balanceBasis <= 0) balanceBasis = 1000;

      const baseAmount = isPercent ? (balanceBasis * (Number(form.entry_amount) / 100)) : (Number(form.entry_amount) || 0);
      const cost = baseAmount * Math.pow(Number(form.multiplier), i);
      const dist = i === 0 ? 0 : (form.use_disparity_gap ? Number(form.step_pct) * (1 + (Math.max(0, i - 1) * Number(form.disparity_factor) / 100)) : Number(form.step_pct)) || 0;
      cumulativeDist += dist;

      // Calculate target price based on side and cumulative distance
      let targetPrice = 0;
      if (latestPrice > 0) {
        targetPrice = bot.side === 'short'
          ? latestPrice * (1 + cumulativeDist / 100)
          : latestPrice * (1 - cumulativeDist / 100);
      }

      return {
        step_number: i + 1,
        target_price_pct: Number(cumulativeDist.toFixed(2)),
        target_qty: Number(cost.toFixed(4)),
        target_price: Number(targetPrice.toFixed(bot.price_precision || 2))
      };
    });
    setForm({ ...form, custom_targets: newTargets });
    setScalingMode('custom');
  };

  const handleEntryTypeChange = (newType: string) => {
    if (form.entry_type === newType) return;
    let balanceBasis = Number(bot.principal_amount || form.balance || bot.total_equity || 0);
    if (balanceBasis <= 0) balanceBasis = 1000;
    const currentVal = Number(form.entry_amount);
    let newVal = currentVal;
    if (newType === 'percent') newVal = Number(((currentVal / balanceBasis) * 100).toFixed(2));
    else newVal = Number((balanceBasis * (currentVal / 100)).toFixed(2));
    setForm(prev => ({ ...prev, entry_type: newType, entry_amount: newVal }));
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const dataToSave = {
        ...form,
        scaling_mode: scalingMode,
        tp_threshold_step: form.use_dynamic_tp ? form.tp_threshold_step : 0,
        tp_threshold_pct: form.use_dynamic_tp ? form.tp_threshold_pct : 0
      };
      const res = await fetch(`/dca/${bot.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as any)?.content || '' },
        body: JSON.stringify({ bot: dataToSave })
      });
      const data = await res.json();
      if (data.ok) { onSaved(data.bot); setMessage({ type: 'ok', text: '저장 성공!' }); }
      else { setMessage({ type: 'err', text: data.errors?.join(', ') || '저장 실패' }); }
    } catch (e: any) { setMessage({ type: 'err', text: e.message }); }
    finally { setSaving(false); }
  };

  const row = (label: string, inputEl: React.ReactNode, helper?: string) => (
    <div className="flex flex-col md:flex-row md:items-start gap-1 md:gap-4">
      <div className="md:w-36 shrink-0 pt-2">
        <label className="text-xs font-bold text-muted-foreground">{label}</label>
        {helper && <p className="text-[10px] text-muted-foreground/50 font-medium leading-tight mt-0.5">{helper}</p>}
      </div>
      <div className="flex-1 w-full">{inputEl}</div>
    </div>
  );

  const field = (key: string) => ({
    value: (form as any)[key] ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { type, value } = e.target;
      setForm((f: any) => ({ ...f, [key]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value }));
    }
  });

  return (
    <div className="space-y-8 bg-card rounded-3xl border border-border shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
      <StrategyPresets applyPreset={applyPreset} isRunning={isRunning} />

      <div className="p-8 space-y-10">
        {isReadOnly && (
          <div className="flex items-center gap-2 p-3 rounded-xl text-sm font-bold bg-amber-500/10 text-amber-600 border border-amber-500/10">
            <AlertCircle className="w-4 h-4 shrink-0" /> 봇이 비활성화되거나 보관된 상태에서는 설정을 변경할 수 없습니다.
          </div>
        )}

        {message && (
          <div className={`flex items-center gap-2 p-3 rounded-xl text-sm font-bold ${message.type === 'ok' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
            {message.type === 'ok' ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />} {message.text}
          </div>
        )}

        {minNotionalWarning && (
          <div className="flex items-center gap-2 p-3 rounded-xl text-[11px] font-bold bg-danger/10 text-danger border border-danger/20">
            <AlertCircle className="w-4 h-4 shrink-0" /> <span>{minNotionalWarning}</span>
          </div>
        )}

        {/* Modular Sections */}
        <BasicSettings form={form} setForm={setForm} row={row} field={field} isReadOnly={isReadOnly} isMarginReadOnly={isMarginReadOnly} isRunning={isRunning} hasPosition={hasPosition} bot={bot} currency={currency} />

        <ScalingSettings form={form} setForm={setForm} row={row} field={field} isReadOnly={isReadOnly} scalingMode={scalingMode} setScalingMode={setScalingMode} showCustomTargets={showCustomTargets} setShowCustomTargets={setShowCustomTargets} bot={bot} currency={currency} syncAutoToCustom={syncAutoToCustom} handleEntryTypeChange={handleEntryTypeChange} />

        <RiskSettings form={form} setForm={setForm} row={row} field={field} isReadOnly={isReadOnly} />

        {/* 1시간봉 횡보 필터 메인 패널 직접 노출 */}
        <div className="bg-muted/10 border border-border rounded-2xl p-4 space-y-4">
          <h4 className="text-[11px] font-black text-foreground uppercase">1시간봉 횡보 장세 필터</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-3 bg-card hover:bg-danger/5 rounded-xl border border-border cursor-pointer transition-all animate-in fade-in duration-200" onClick={() => setForm((f: any) => ({ ...f, use_1h_sideways_filter: !f.use_1h_sideways_filter }))}>
              <input type="checkbox" className="w-4 h-4 rounded text-danger" checked={form.use_1h_sideways_filter} readOnly />
              <div className="flex-1">
                <span className="text-[11px] font-bold text-foreground block">1시간봉 횡보 장세 필터 (Entry)</span>
                <span className="text-[9px] text-muted-foreground">미시 추세 급격 시 역방향 진입 차단</span>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-card hover:bg-danger/5 rounded-xl border border-border cursor-pointer transition-all animate-in fade-in duration-200" onClick={() => setForm((f: any) => ({ ...f, use_1h_sideways_scaling_filter: !f.use_1h_sideways_scaling_filter }))}>
              <input type="checkbox" className="w-4 h-4 rounded text-danger" checked={form.use_1h_sideways_scaling_filter} readOnly />
              <div className="flex-1">
                <span className="text-[11px] font-bold text-foreground block">1시간봉 횡보 물타기 필터 (Scaling)</span>
                <span className="text-[9px] text-muted-foreground">미시 추세 진행 중 무리한 물타기 차단</span>
              </div>
            </div>
          </div>
          <div className="bg-card/50 rounded-xl p-3 border border-border/50 text-[9px] text-muted-foreground leading-relaxed space-y-1.5 animate-in fade-in duration-300">
            <p className="font-bold text-foreground flex items-center gap-1">💡 횡보 장세 필터 작동 원리 (1시간봉 ADX 22 미만 횡보 국면 시 활성화)</p>
            <p>• <span className="text-foreground font-semibold">Entry (첫 진입 차단)</span>: 가격이 기준선(EMA) 아래로 밀려나면서 극과매도(Stoch &lt; 20) 상태일 때 롱 진입을 차단합니다. 즉, 쏟아지는 칼날을 바로 잡지 않고 하락이 멈춘 뒤 과매도를 탈출하며 반등하기 시작할 때(Stoch &gt;= 20) 진입합니다.</p>
            <p>• <span className="text-foreground font-semibold">Scaling (물타기 차단)</span>: 단기 추세가 진입 방향과 반대로 강하게 꽂히고 있을 때, 바닥 확인 없이 물타기를 연달아 체결하는 것을 제한하여 시드를 방어합니다.</p>
          </div>
        </div>

        {/* 특정 시간대/요일 매수 회피 필터 직접 노출 */}
        <div className="bg-muted/10 border border-border rounded-2xl p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h4 className="text-[11px] font-black text-foreground uppercase">특정 시간대 & 요일 매수 회피 설정</h4>
          </div>

          {/* 특정 시간대 (30분 단위 다중 선택) */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => setForm((f: any) => ({ ...f, use_skip_hours: !f.use_skip_hours }))}>
              <input type="checkbox" className="w-4 h-4 rounded text-primary cursor-pointer" checked={form.use_skip_hours} readOnly />
              <span className="text-[11px] font-bold text-foreground cursor-pointer">특정 시간대 신규매수 차단 활성화 (KST 기준)</span>
            </div>
            {form.use_skip_hours && (
              <div className="p-4 bg-card border border-border rounded-xl space-y-3 animate-in fade-in duration-200">
                <p className="text-[9px] text-muted-foreground font-medium">진입을 방지할 시간대를 모두 선택하세요 (30분 단위)</p>
                <div className="max-h-36 overflow-y-auto pr-1 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1.5">
                  {Array.from({ length: 48 }).map((_, idx) => {
                    const pad = (n: number) => n.toString().padStart(2, '0');
                    const hour = Math.floor(idx / 2);
                    const min = idx % 2 === 0 ? '00' : '30';
                    const timeLabel = `${pad(hour)}:${min}`;
                    const isSelected = form.skip_time_segments.includes(idx);
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          const newSegments = isSelected
                            ? form.skip_time_segments.filter((s: number) => s !== idx)
                            : [...form.skip_time_segments, idx];
                          setForm((f: any) => ({ ...f, skip_time_segments: newSegments }));
                        }}
                        className={`py-1.5 text-[9px] font-bold rounded-lg border transition-all ${isSelected ? 'bg-danger text-white border-danger shadow-sm shadow-danger/10' : 'bg-background hover:border-danger/30 text-foreground border-border'}`}
                      >
                        {timeLabel}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* 특정 요일 (다중 선택) */}
          <div className="space-y-3 pt-4 border-t border-border/50">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => setForm((f: any) => ({ ...f, use_skip_days: !f.use_skip_days }))}>
              <input type="checkbox" className="w-4 h-4 rounded text-primary cursor-pointer" checked={form.use_skip_days} readOnly />
              <span className="text-[11px] font-bold text-foreground cursor-pointer">특정 요일 매수 차단 활성화 (KST 기준)</span>
            </div>
            {form.use_skip_days && (
              <div className="p-4 bg-card border border-border rounded-xl space-y-3 animate-in fade-in duration-200">
                <p className="text-[9px] text-muted-foreground font-medium">진입을 방지할 요일을 선택하세요</p>
                <div className="flex flex-wrap gap-2">
                  {['일', '월', '화', '수', '목', '금', '토'].map((day, idx) => {
                    const isSelected = form.skip_days.includes(idx);
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          const newDays = isSelected
                            ? form.skip_days.filter((d: number) => d !== idx)
                            : [...form.skip_days, idx];
                          setForm((f: any) => ({ ...f, skip_days: newDays }));
                        }}
                        className={`px-4 py-2 text-[10px] font-black rounded-xl border transition-all ${isSelected ? 'bg-danger text-white border-danger shadow-sm' : 'bg-background hover:border-danger/30 text-foreground border-border'}`}
                      >
                        {day}요일
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <FilterSettings form={form} setForm={setForm} row={row} />

        {/* Telegram Notification Settings */}
        <NotificationSettings
          telegramEnabled={form.telegram_enabled}
          setTelegramEnabled={(val) => setForm(f => ({ ...f, telegram_enabled: val }))}
          telegramNotifyTypes={form.telegram_notify_types}
          setTelegramNotifyTypes={(val) => setForm(f => ({ ...f, telegram_notify_types: val }))}
          isLive={form.is_live}
          isReadOnly={isReadOnly}
        />

        <div className="flex justify-end pt-8 border-t border-border mt-10 gap-3">
          {onClone && (
            <Button
              type="button"
              onClick={() => onClone(form)}
              variant="outline"
              className="h-12 rounded-2xl border-primary text-primary hover:bg-primary/10 font-black px-6 transition-all"
            >
              <Copy className="w-4 h-4 mr-2" /> 복제하여 새로 생성
            </Button>
          )}
          <Button onClick={save} disabled={saving || isReadOnly} className="h-12 rounded-2xl bg-primary text-primary-foreground font-black hover:opacity-90 px-12 shadow-lg shadow-primary/20 transition-all active:scale-95 disabled:opacity-50">
            <Save className="w-4 h-4 mr-2" /> {saving ? '저장 중...' : (isReadOnly ? '수정 불가' : '설정 저장')}
          </Button>
        </div>
      </div>
    </div>
  );
};
